import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSeoValue } from "@/components/seo/SeoContext";

/**
 * SeoHead — GLOBAL, single source of truth for the document <head> SEO
 * on the JS-rendered SPA (2026-06-15).
 *
 * BACKGROUND / BUG THIS FIXES:
 * Production's served index.html ships baked-in homepage SEO (a
 * `<link rel="canonical" href="/">`, hreflang to `/`, a homepage
 * `<title>` and description) WITHOUT the `data-seo-early` /
 * `data-static-default` markers that `App.js` strips. Combined with the
 * per-route Helmet, that produced DUPLICATE canonical/title/description
 * and a WRONG canonical (pointing to `/`) on route pages — exactly the
 * "duplicate without user-selected canonical" / "Google chose a
 * different canonical" noise in Search Console.
 *
 * Fix = centralise head management in Helmet. The SPA shell
 * (public/index.html) ships NO baked SEO tags (no static title/description/
 * OG, no early-SEO canonical script), so Helmet is the ONLY source on the
 * client and there is never a duplicate. This component provides, for
 * EVERY path, the canonical + hreflang + site-default title/description/
 * OG — mirroring 1:1 the inline early-SEO script in index.html and the
 * backend bot-prerender, so static fetch, JS render and prerender all
 * agree on a single canonical. Per-page components (e.g. RouteSeo) only
 * OVERRIDE title/description/OG (Helmet: innermost wins); the canonical
 * is emitted ONLY here so there is never more than one.
 */

const BASE = "https://mototrips.net";
const SUPPORTED = ["pt", "en", "es", "fr"];
const OG_LOCALE = { pt: "pt_PT", en: "en_US", es: "es_ES", fr: "fr_FR" };
const DEFAULT_TITLE = "MotoTrips — Planeia, Regista e Partilha os Teus Passeios de Mota!";
const DEFAULT_DESC =
  "Descobre passeios de mota partilhados pela comunidade MotoTrips. Explora rotas, galerias de fotos e aventuras de motards e seus clubes!";
const DEFAULT_IMAGE = "https://mototrips.net/og-invite.png";

export const SeoHead = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  // Per-page override pushed by page-level components (RouteSeo, the legacy
  // <SEO>, …) via SeoContext. SeoHead is the SINGLE emitter of title /
  // description / OG / canonical / hreflang / twitter so the rendered DOM
  // never has duplicates (react-helmet-async@3.0.0 does NOT dedupe across
  // separate <Helmet> instances). Override fields: title, description,
  // image, type, robots, path (clean canonical path), langs (offered set).
  const seo = useSeoValue();

  const sp = new URLSearchParams(location.search);
  let currentLng = sp.get("lng");
  if (!SUPPORTED.includes(currentLng)) currentLng = "pt";

  // Languages OFFERED on this page (page-SEO may restrict, e.g. PT-only
  // pages). Defaults to all supported.
  const langs = (seo?.langs && seo.langs.length)
    ? seo.langs.filter((l) => SUPPORTED.includes(l))
    : SUPPORTED;
  // Canonical path: an explicit page path wins; else the real pathname.
  // Non-lng query params are intentionally dropped so variants share one
  // canonical (kills the GSC "duplicate / alternate canonical" noise).
  let basePath = seo?.path ? seo.path : (location.pathname || "/");
  // Normalise a trailing slash away (except root "/") so "/passeio/x/" and
  // "/passeio/x" resolve to a SINGLE canonical (GSC duplicate fix).
  if (basePath.length > 1) basePath = basePath.replace(/\/+$/, "");
  const urlFor = (lng) => `${BASE}${basePath}${lng !== "pt" ? `?lng=${lng}` : ""}`;
  // If the active language isn't offered here, canonical falls back to pt.
  const effLng = langs.includes(currentLng) ? currentLng : "pt";
  const canonical = urlFor(effLng);

  const active = String(i18n.language || "").slice(0, 2).toLowerCase();
  const htmlLang = langs.includes(active) ? active : effLng;

  // Per-page override wins; else site defaults.
  const title = seo?.title || DEFAULT_TITLE;
  const description = seo?.description || DEFAULT_DESC;
  const image = seo?.image || DEFAULT_IMAGE;
  const ogType = seo?.type || "website";

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {seo?.robots && <meta name="robots" content={seo.robots} />}

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={urlFor("pt")} />
      {langs.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={urlFor(l)} />
      ))}

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="MotoTrips" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={OG_LOCALE[effLng] || OG_LOCALE.pt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default SeoHead;
